import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, BookOpen, Quote, MessageSquare, CalendarHeart, Layers, Pencil, ArrowLeft, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import Reveal from "@/components/Reveal";

const monthYear = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleDateString("en-US", { month: "long", year: "numeric" }); } catch { return ""; } };
const evDate = (d) => { if (!d) return ""; try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return d; } };

const Section = ({ title, children, testid }) => (
  <div className="bg-mahogany-800/40 gold-frame rounded-sm p-8" data-testid={testid}>
    <p className="overline mb-5">{title}</p>
    {children}
  </div>
);

export default function MemberProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setP(null); setError("");
    api.get(`/members/${id}/profile`).then(({ data }) => setP(data)).catch((e) => setError(e.response?.status === 404 ? "Member not found." : "Unable to load this profile."));
  }, [id]);

  if (error) return <div className="min-h-screen bg-mahogany-900 pt-40 text-center text-cream/60" data-testid="profile-error">{error} <div className="mt-6"><Link to="/dashboard" className="text-fuchsia">Back to Dashboard</Link></div></div>;
  if (!p) return <div className="min-h-screen bg-mahogany-900" />;

  const act = p.activity || {};
  const loc = [p.city, p.state].filter(Boolean).join(", ");
  const hasReadingLife = (p.genres?.length || p.favorite_book || p.favorite_author || p.currently_reading || p.book_that_changed_me || p.want_to_read);

  return (
    <div className="min-h-screen bg-gradient-to-b from-mahogany-950 to-mahogany-900 pt-28 pb-20 grain" data-testid="member-profile-page">
      {/* Header */}
      <div className="relative">
        <div className="absolute inset-0 h-56 bg-gradient-to-br from-mahogany-800 to-mahogany-950 border-b border-gold/15" />
        <div className="relative max-w-4xl mx-auto px-6 pt-16">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-cream/60 hover:text-gold text-sm mb-8" data-testid="back-to-dashboard"><ArrowLeft size={16} /> Dashboard</Link>
          <div className="flex flex-col sm:flex-row sm:items-end gap-6">
            <Avatar src={p.picture} name={p.display_name} size={128} className="!ring-2 !ring-gold/60 shadow-[0_10px_40px_rgba(0,0,0,0.5)]" />
            <div className="flex-1">
              <h1 className="font-serif text-4xl sm:text-5xl text-cream leading-none">{p.display_name}</h1>
              <div className="flex flex-wrap items-center gap-4 mt-3 text-cream/60 text-sm">
                {loc && <span className="flex items-center gap-1.5"><MapPin size={14} className="text-gold" /> {loc}</span>}
                {p.member_since && <span className="flex items-center gap-1.5"><Sparkles size={14} className="text-gold" /> Member since {monthYear(p.member_since)}</span>}
              </div>
            </div>
            {p.can_edit && (
              <Link to="/profile/edit" className="btn-secondary !py-2.5 !px-6 !text-xs inline-flex items-center gap-2" data-testid="profile-edit-btn"><Pencil size={14} /> Edit Profile</Link>
            )}
          </div>
          {p.account_status === "suspended" && <p className="mt-4 text-red-300 text-sm">This account is currently suspended.</p>}
          {p.bio && <p className="text-cream/75 text-lg font-light leading-relaxed mt-8 max-w-2xl">{p.bio}</p>}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 mt-12 grid md:grid-cols-2 gap-6">
        {hasReadingLife && (
          <Reveal className="md:col-span-2">
            <Section title="Her Reading Life" testid="section-reading-life">
              {p.genres?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {p.genres.map((g) => <span key={g} className="px-4 py-2 border border-gold/30 rounded-full text-cream/80 text-sm">{g}</span>)}
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-6">
                {p.currently_reading && <div><p className="text-gold text-xs uppercase tracking-wider mb-1">Currently Between the Pages Of</p><p className="font-serif text-xl text-cream">{p.currently_reading}</p></div>}
                {p.favorite_book && <div><p className="text-gold text-xs uppercase tracking-wider mb-1">Favorite Book</p><p className="font-serif text-xl text-cream">{p.favorite_book}</p></div>}
                {p.favorite_author && <div><p className="text-gold text-xs uppercase tracking-wider mb-1">Favorite Author</p><p className="font-serif text-xl text-cream">{p.favorite_author}</p></div>}
                {p.want_to_read && <div><p className="text-gold text-xs uppercase tracking-wider mb-1">On Her List</p><p className="font-serif text-xl text-cream">{p.want_to_read}</p></div>}
              </div>
            </Section>
          </Reveal>
        )}

        {p.book_that_changed_me && (
          <Reveal><Section title="A Book That Changed Her" testid="section-changed">
            <BookOpen size={22} className="text-gold mb-3" strokeWidth={1.25} />
            <p className="font-serif text-2xl text-cream">{p.book_that_changed_me}</p>
          </Section></Reveal>
        )}
        {p.favorite_quote && (
          <Reveal><Section title="Words She Lives By" testid="section-quote">
            <Quote size={22} className="text-gold mb-3" strokeWidth={1.25} />
            <p className="font-serif text-xl text-cream italic leading-relaxed">"{p.favorite_quote}"</p>
          </Section></Reveal>
        )}

        {/* Inside the Room */}
        <Reveal className="md:col-span-2">
          <Section title="Inside the Room" testid="section-mahogany-room">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="flex items-center gap-3"><MessageSquare size={20} className="text-gold" strokeWidth={1.25} /><div><p className="font-serif text-2xl text-cream leading-none">{act.discussion_count || 0}</p><p className="text-cream/50 text-xs uppercase tracking-wider">Discussion posts</p></div></div>
              <div className="flex items-center gap-3"><Layers size={20} className="text-gold" strokeWidth={1.25} /><div><p className="font-serif text-2xl text-cream leading-none">{act.seasons?.length || 0}</p><p className="text-cream/50 text-xs uppercase tracking-wider">Seasons joined</p></div></div>
              <div className="flex items-center gap-3"><CalendarHeart size={20} className="text-gold" strokeWidth={1.25} /><div><p className="font-serif text-2xl text-cream leading-none">{act.past_events?.length || 0}</p><p className="text-cream/50 text-xs uppercase tracking-wider">Gatherings attended</p></div></div>
              <div className="flex items-center gap-3"><BookOpen size={20} className="text-gold" strokeWidth={1.25} /><div><p className="font-serif text-2xl text-cream leading-none">{act.discussion_books?.length || 0}</p><p className="text-cream/50 text-xs uppercase tracking-wider">Books discussed</p></div></div>
            </div>

            {act.current_read && (
              <div className="mt-8 pt-6 border-t border-gold/15 flex items-center gap-4">
                <img src={act.current_read.cover_url} alt={act.current_read.title} className="w-14 rounded-sm" />
                <div><p className="text-gold text-xs uppercase tracking-wider mb-0.5">Current Room Read</p><p className="font-serif text-xl text-cream">{act.current_read.title}</p></div>
              </div>
            )}

            {act.attendance_hidden ? (
              <p className="mt-6 text-cream/40 text-sm italic">This member keeps her event attendance private.</p>
            ) : act.upcoming_events?.length > 0 && (
              <div className="mt-8 pt-6 border-t border-gold/15">
                <p className="text-gold text-xs uppercase tracking-wider mb-3">RSVP'd Gatherings</p>
                <div className="space-y-2">
                  {act.upcoming_events.map((e) => (
                    <Link to={`/events/${e.id}`} key={e.id} className="flex items-center justify-between text-cream/80 hover:text-gold text-sm">
                      <span>{e.title}</span><span className="text-cream/40">{evDate(e.date)}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </Section>
        </Reveal>
      </div>
    </div>
  );
}
